/**
 * The caption property panel is a view over caption tracks, not a merged
 * transcript. Imported documents may partition overlapping cues into lanes;
 * each lane keeps its own order, lock, and visibility policy.
 */
export type CaptionPanelTrack = {
  readonly items?: readonly string[];
  readonly hidden?: boolean;
  readonly locked?: boolean;
  readonly metadata?: { readonly type?: string };
};
export type CaptionPanelItems = Readonly<Record<string, {readonly type:string}>>;

export type CaptionPanelTrackOwner<TTrack extends CaptionPanelTrack> = {
  readonly track: TTrack;
  readonly trackIndex: number;
  readonly itemIndex: number;
};

export type CaptionPanelSceneItem = {
  readonly sceneId: number | null;
  readonly startMs: number;
};

export type CaptionPanelSceneGroup = {
  readonly sceneId: number | null;
  readonly itemIds: string[];
  readonly trackIndex: number;
  readonly trackLocked: boolean;
  readonly trackHidden: boolean;
};

export function getCaptionPanelTracks<TTrack extends CaptionPanelTrack>(
  tracks: readonly TTrack[] | null | undefined,
  items?: CaptionPanelItems,
): TTrack[] {
  return (tracks ?? []).filter((track) => track.metadata?.type === 'caption' || track.items?.some(id=>items?.[id]?.type==='captions'));
}

/** Returns an item's actual caption lane, preserving lane-local keyboard order. */
export function findCaptionPanelTrackOwner<TTrack extends CaptionPanelTrack>(
  tracks: readonly TTrack[] | null | undefined,
  itemId: string,
  items?: CaptionPanelItems,
): CaptionPanelTrackOwner<TTrack> | null {
  for (let trackIndex = 0; trackIndex < (tracks?.length ?? 0); trackIndex++) {
    const track = tracks?.[trackIndex];
    if (!track || (track.metadata?.type !== 'caption' && items?.[itemId]?.type !== 'captions')) continue;
    const itemIndex = track.items?.indexOf(itemId) ?? -1;
    if (itemIndex >= 0) return { track, trackIndex, itemIndex };
  }
  return null;
}

/** A presentation merge follows lane adjacency, not the sorted panel rows. */
export function getCaptionPanelNextItemIds<TTrack extends CaptionPanelTrack>(
  tracks: readonly TTrack[] | null | undefined,
  items?: CaptionPanelItems,
): ReadonlyMap<string, string | undefined> {
  const nextIds = new Map<string, string | undefined>();
  for (const track of getCaptionPanelTracks(tracks,items)) {
    track.items?.forEach((id, index) => {
      if (!nextIds.has(id)) nextIds.set(id, track.items?.[index + 1]);
    });
  }
  return nextIds;
}

/** Hidden lanes remain editable in the panel but must not drive playback scroll. */
export function getVisibleCaptionPanelItemIds<TTrack extends CaptionPanelTrack>(
  tracks: readonly TTrack[] | null | undefined,
  items?: CaptionPanelItems,
): string[] {
  return getCaptionPanelTracks(tracks,items).flatMap((track) =>
    track.hidden ? [] : [...(track.items ?? [])].filter(id=>!items||items[id]?.type==='captions'),
  );
}

/**
 * Order cues before grouping: imported assets may have no sceneId, so grouping
 * an entire lane first would put later cues ahead of earlier cues on other lanes.
 * Only consecutive cues from the same lane and scene share a panel group.
 */
export function getCaptionPanelSceneGroups<TTrack extends CaptionPanelTrack>(
  tracks: readonly TTrack[] | null | undefined,
  getItem: (itemId: string) => CaptionPanelSceneItem | null,
): CaptionPanelSceneGroup[] {
  const entries: Array<Omit<CaptionPanelSceneGroup, 'itemIds'> & {
    readonly itemId: string;
    readonly startMs: number;
    readonly itemIndex: number;
  }> = [];

  (tracks??[]).forEach((track, trackIndex) => {
    (track.items ?? []).forEach((itemId, itemIndex) => {
      const item = getItem(itemId);
      if (!item) return;
      entries.push({
          sceneId: item.sceneId,
          itemId,
          trackIndex,
          trackLocked: track.locked ?? false,
          trackHidden: track.hidden ?? false,
          startMs: item.startMs,
          itemIndex,
        });
    });
  });

  const groups: CaptionPanelSceneGroup[] = [];
  entries
    .sort((left, right) =>
      left.startMs - right.startMs
      || left.trackIndex - right.trackIndex
      || left.itemIndex - right.itemIndex,
    )
    .forEach(({itemId, startMs: _startMs, itemIndex: _itemIndex, ...entry}) => {
      const previous = groups[groups.length - 1];
      if (!previous || previous.trackIndex !== entry.trackIndex || previous.sceneId !== entry.sceneId) {
        groups.push({...entry, itemIds: [itemId]});
      } else previous.itemIds.push(itemId);
    });
  return groups;
}
